import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

async function linearQuery<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("linear-proxy", {
    body: { query, variables },
  });
  if (error) throw error;
  if (data?.errors?.length) throw new Error(data.errors[0].message);
  return data.data as T;
}

const PROJECTS_QUERY = `
  query {
    projects(first: 50) {
      nodes {
        id
        name
        description
        status { type color name }
        progress
        targetDate
        startDate
        members {
          nodes { id name }
        }
        teams {
          nodes { id name color }
        }
        url
      }
    }
  }
`;

const ISSUES_QUERY = `
  query ListIssues($projectId: ID) {
    issues(
      first: 100
      filter: { project: { id: { eq: $projectId } } }
      orderBy: updatedAt
    ) {
      nodes {
        id
        identifier
        title
        priority
        priorityLabel
        state { id name color type }
        assignee { id name }
        project { id name }
        createdAt
        updatedAt
        dueDate
        url
      }
    }
  }
`;

const ALL_ISSUES_QUERY = `
  query {
    issues(first: 100, orderBy: updatedAt) {
      nodes {
        id
        identifier
        title
        priority
        priorityLabel
        state { id name color type }
        assignee { id name }
        project { id name }
        createdAt
        updatedAt
        dueDate
        url
      }
    }
  }
`;

export interface LinearProject {
  id: string;
  name: string;
  description: string | null;
  status: { type: string; color: string; name: string };
  progress: number;
  targetDate: string | null;
  startDate: string | null;
  members: { nodes: { id: string; name: string }[] };
  teams: { nodes: { id: string; name: string; color: string }[] };
  url: string;
}

export interface LinearIssue {
  id: string;
  identifier?: string;
  title: string;
  priority: number;
  priorityLabel: string;
  state: { id: string; name: string; color: string; type: string };
  assignee: { id: string; name: string } | null;
  project: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
  dueDate?: string | null;
  url: string;
}

export function useLinearProjects() {
  return useQuery({
    queryKey: ["linear", "projects"],
    queryFn: () => linearQuery<{ projects: { nodes: LinearProject[] } }>(PROJECTS_QUERY),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
}

export function useLinearIssues(projectId?: string | null) {
  return useQuery({
    queryKey: ["linear", "issues", projectId ?? "all"],
    queryFn: () =>
      projectId
        ? linearQuery<{ issues: { nodes: LinearIssue[] } }>(ISSUES_QUERY, { projectId })
        : linearQuery<{ issues: { nodes: LinearIssue[] } }>(ALL_ISSUES_QUERY),
    staleTime: 1000 * 60 * 2,
    retry: false,
  });
}
